import { Button, spacingHelper, typographyHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { GovernmentDataPartnerBannerWithData } from './subcomponents/GovernmentDataPartnerBannerWithData';
import { MoreInformation, OverlaySummaryBrick } from './subcomponents/OverlaySummaryBrick';
import type { OverlayTilesItem } from './subcomponents/OverlayTiles';
import { OverlayTiles } from './subcomponents/OverlayTiles';
import { DynamicText, TextWithStyles } from '../../../app-kit/components/DynamicText/DynamicText';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { mapDynamicContentItems } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { ResultOf } from 'gql.tada';
import { buttonFragment } from '../../../graphql/fragments/buttonFragment';
import { weblinkFragment } from '../../../graphql/fragments/weblinkFragment';
import { textFragment } from '../../../graphql/fragments/textFragment';

type ButtonFragment = ResultOf<typeof buttonFragment>;
type WebLinkFragment = ResultOf<typeof weblinkFragment>;
type TextFragment = ResultOf<typeof textFragment>;

const LandDescription = styled.div`
  margin-bottom: ${spacingHelper('medium')};
`;

const ShowMoreButton = styled(Button)`
  ${typographyHelper('overline')};
  display: block;
  margin: ${spacingHelper('medium auto')};
`;

const MoreInformationWrapper = styled.div`
  margin-bottom: ${spacingHelper('small')};
`;

interface OverlaySummary {
  title: string;
  overlayTypeSummaries: { overlayType: string; overlays: string[] }[];
  moreInformation: (ButtonFragment | TextFragment | WebLinkFragment)[];
}

interface GovernmentDataBrickProps {
  title: string;
  subtitle?: string | null;
  landDescription?: TextWithStyles[] | null;
  overlayTiles?: OverlayTilesItem[] | null;
  overlaySummary?: OverlaySummary | null;
  showMoreButton?: string | null;
  moreInformation?: (ButtonFragment | TextFragment | WebLinkFragment)[] | null;
}

export const GovernmentDataBrick: FunctionComponent<GovernmentDataBrickProps> = ({
  title,
  subtitle,
  landDescription,
  overlayTiles,
  overlaySummary,
  showMoreButton,
  moreInformation,
}) => {
  const { displayModal } = usePropertyPageModalContext();
  const [hideSummary, setHideSummary] = useState<boolean>(true);

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      headerChildren={
        <DisclaimerIcon
          title="Open Government Data Disclaimer"
          onClick={() => {
            displayModal({ name: 'governmentDataDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__government_data_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      {landDescription && (
        <LandDescription>
          <DynamicText
            dynamicTexts={landDescription}
            variant="body01"
            variantBold="body01Bold"
            colorVariant="textSecondary"
          />
        </LandDescription>
      )}
      {overlayTiles && <OverlayTiles tiles={overlayTiles} />}
      {moreInformation && (
        <MoreInformationWrapper>
          <MoreInformation
            dynamicContents={mapDynamicContentItems(moreInformation)}
            textColorVariant="textSecondary"
          />
        </MoreInformationWrapper>
      )}
      {overlaySummary && !hideSummary && (
        <OverlaySummaryBrick
          title={overlaySummary.title}
          overlayTypeSummaries={overlaySummary.overlayTypeSummaries}
          moreInformation={mapDynamicContentItems(overlaySummary.moreInformation)}
        />
      )}
      {hideSummary && showMoreButton && (
        <ShowMoreButton variant="link-primary" onClick={() => setHideSummary(false)}>
          {showMoreButton}
        </ShowMoreButton>
      )}
      <GovernmentDataPartnerBannerWithData />
    </ContentBrick>
  );
};
